import { css } from '@emotion/react'
import { Button, Heading, HFlow, Icon, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { HLabel } from 'components/HLabel'
import { ReactNode } from 'react'
import { formatDateAndHoursMinutes } from 'util/date/formatDate'

import { MChatFooterInfo } from '../mchat/components/MChatFooterInfo'
import { MChatAnteriorFooterInfoModel } from '../mchat/model-mchat'
import { MCHAT_RF_RESULTADO_BY_STATUS, MChatRfResultadoStatus } from '../model-mChatRf'

type MChatRfRegistroListItemType = 'm-chat' | 'consulta-seguimento'

interface MChatRfRegistroListItemProps {
  type: MChatRfRegistroListItemType
  status: MChatRfResultadoStatus
  title: string
  label: string
  description: ReactNode
  footerInfo: MChatAnteriorFooterInfoModel
  isLastItem: boolean
  onPrint: (event: React.MouseEvent<HTMLButtonElement>) => void
  cidadaoDataNascimento: LocalDate
}

export function MChatRfRegistroListItem(props: Readonly<MChatRfRegistroListItemProps>) {
  const { type, status, title, label, description, footerInfo, isLastItem, onPrint, cidadaoDataNascimento } = props

  const { colorScale, icon } = MCHAT_RF_RESULTADO_BY_STATUS[status]

  const theme = useTheme()
  const styles = createStyles(theme, colorScale)

  const formattedDate = formatDateAndHoursMinutes(footerInfo.dataResultado)

  return (
    <HFlow hSpacing={0.5}>
      <VFlow vSpacing={0} style={styles.iconWrapper}>
        <Icon icon={icon} style={styles.icon} />
        {!isLastItem && <div css={styles.line} />}
      </VFlow>
      <VFlow vSpacing={0.5}>
        <HFlow alignItems='center' justifyContent='space-between'>
          <HFlow hSpacing={0.175} alignItems='baseline'>
            <Heading level={4} fontWeight='normal' style={styles.title}>
              <strong>{title}</strong> ({label})
            </Heading>
            {' | '}
            <Text style={styles.subtitle}>
              <strong>{type === 'consulta-seguimento' ? 'Entrevista de seguimento' : 'M-CHAT-R™'}</strong> avaliado{' '}
              {formattedDate}
            </Text>
          </HFlow>
          <Tooltip text='Imprimir avaliação completa'>
            <Button size='small' skin='ghost' onClick={onPrint}>
              <Icon icon='printerOutline' />
            </Button>
          </Tooltip>
        </HFlow>
        <VFlow vSpacing={0.25}>
          <HLabel title='Encaminhamento:' style={styles.description}>
            {description}
          </HLabel>
          <MChatFooterInfo info={footerInfo} style={styles.footerInfo} cidadaoDataNascimento={cidadaoDataNascimento} />
        </VFlow>
      </VFlow>
    </HFlow>
  )
}

const createStyles = (theme: Theme, colorScale: ColorScale) => ({
  iconWrapper: css`
    display: grid;
    grid-template-rows: 1.75rem 1fr;
  `,
  icon: css`
    margin-top: 0.25rem;
    fill: ${colorScale.c40};
  `,
  line: css`
    width: 2px;
    height: calc(100% + 1.25rem);
    background-color: ${theme.pallete.divider};
    margin-left: calc(0.75rem - 1px);
  `,
  title: css`
    color: ${colorScale.c40};
  `,
  subtitle: css`
    color: ${theme.pallete.gray.c40};
  `,
  description: css`
    & strong {
      font-weight: normal;
    }
  `,
  footerInfo: css`
    color: ${theme.pallete.gray.c40};
    & strong {
      font-weight: normal;
    }
  `,
})
